import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Stack,
  Drawer,
  Divider,
  Typography,
  TextField,
  Box,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

// components
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Close, UploadFile } from "@mui/icons-material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useSnackbar } from "notistack";
import { LoadingButton } from "@mui/lab";
import Scrollbar from "../../../components/scrollbar";
import { UserSchema } from "../../../forms-schema/form.schema";
import { useUpdateUserMutation } from "../../../redux/services/user.service";
import { useUploadSingleFileMutation } from "../../../redux/services/upload.service";

UserForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
  openRow: PropTypes.object,
};

const supportedFileTypes = [
  // "text/csv",
  "application/vnd.ms-excel",
  "application/pdf",
  "image/jpeg",
];

export default function UserForm({ open, onClose, edit, openRow }) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    watch,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(UserSchema),
  });

  const { gender, occupation, education } = watch();

  const fileRef = useRef();

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBuffer, setFileBuffer] = useState([]);
  const [fileError, setFileError] = useState("");
  const [uploadFile, { isLoading: isFileUploading }] =
    useUploadSingleFileMutation();

  const [updateUser, { isLoading: isUpdateLoading }] = useUpdateUserMutation();

  console.log(selectedFile);

  const handleClick = async (values) => {
    console.log("Values:", values);

    const uploadResult = await uploadFile(selectedFile);

    const updatedValues = {
      ...values,
      otherDisabilities: values.otherDisabilities
        ? values.otherDisabilities.split(",").map((item) => item.trim())
        : [], // Split by comma and trim any extra spaces
      disabilityCertificateUrl: uploadResult?.data?.data?.Location,
    };

    const resultAction = await updateUser({ ...updatedValues, id: openRow.id });
    if (resultAction?.data?.success) {
      reset();
      setSelectedFile(null); // Clear the file when successfully submitted
      onClose();
    }
  };

  console.log("selectedFile");

  useEffect(() => {
    if (!open) {
      setSelectedFile(null); // Reset selectedFile when the drawer is closed
    }
  }, [open]);

  useEffect(() => {
    reset();
    if (edit) {
      console.log(openRow);
      setValue("firstName", openRow?.firstName);
      setValue("lastName", openRow?.lastName);
      setValue("email", openRow?.email);
      setValue("gender", openRow?.gender);
      setValue("occupation", openRow?.occupation);
      setValue("education", openRow?.education);
      setValue("dateOfBirth", openRow?.dateOfBirth);
      setValue("contactNumber", openRow?.contactNumber);
      setValue("address", openRow?.address);

      setFileError("");
    }
  }, [edit, openRow, setValue, open, reset]);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    try {
      const file = e.dataTransfer.files[0];

      // Check if a file is actually dropped
      if (!file) {
        enqueueSnackbar("No file selected", {
          variant: "error", // Display error message
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      // Check if the file type is supported
      if (!supportedFileTypes.includes(file.type)) {
        setSelectedFile(null);
        enqueueSnackbar("Unsupported file format", {
          variant: "error", // Display error message
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      // Clear any previous error and set the selected file
      setFileError("");
      setSelectedFile(file);
    } catch (error) {
      // Catch and log any errors
      console.error("Error while handling file drop:", error);

      // Show a generic error message using notistack
      enqueueSnackbar("An error occurred while processing the file.", {
        variant: "error", // Display error message
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }
  };

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      console.log("file", file);

      const reader = new FileReader(file?.name);

      const buffer = await file.arrayBuffer();
      console.log("FileBuffer:", buffer);

      const bufferBytes = new Uint8Array(buffer);

      console.log("BufferButes:", bufferBytes);
      setFileBuffer(bufferBytes);

      console.log("FileBuffer", buffer);

      // Check if the file exists
      if (!file) {
        enqueueSnackbar("No file selected", {
          variant: "error", // Display error message
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      // Check if the file type is supported
      if (!supportedFileTypes.includes(file.type)) {
        setSelectedFile(null);
        enqueueSnackbar("Unsupported file format", {
          variant: "error", // Display error message
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      // Clear the error and set the selected file
      setFileError("");
      setSelectedFile(file);
    } catch (error) {
      // Catch and log any errors
      console.error("Error while handling file change:", error);

      // Show a generic error message using notistack
      enqueueSnackbar("An error occurred while processing the file.", {
        variant: "error", // Display error message
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: 360, border: "none", overflow: "hidden" },
        }}
      >
        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            <div>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle1">
                  {edit ? "Edit" : "Create"} User
                </Typography>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Box>
              <Divider />
            </div>
            <form onSubmit={handleSubmit(handleClick)}>
              <Stack spacing={3}>
                <TextField
                  name="firstName"
                  label="First Name"
                  {...register("firstName", { required: true })}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  inputProps={{
                    "data-testid": "firstName",
                  }}
                />

                <TextField
                  name="lastName"
                  label="Last Name"
                  {...register("lastName", { required: true })}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  inputProps={{
                    "data-testid": "lastName",
                  }}
                />

                {/* Gender */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label-type">
                      Gender
                    </InputLabel>
                    <Select
                      {...register("gender")}
                      labelId="demo-simple-select-label-type"
                      label="Gender"
                      error={Boolean(errors.type)}
                      value={gender}
                      inputProps={{ "data-testid": "gender" }}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* BirthDate */}
                <Box>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Controller
                      name="dateOfBirth"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          label="Birth Date"
                          format="DD/MM/YYYY"
                          value={field.value ? moment(field.value) : null}
                          onChange={(date) => {
                            setValue(
                              "dateOfBirth",
                              date ? date.format("DD/MM/YYYY") : "",
                            );
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: Boolean(errors.birthDate),
                              helperText: errors.birthDate
                                ? errors.birthDate.message
                                : "",
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Box>

                <TextField
                  name="email"
                  label="Email"
                  {...register("email", { required: true })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  inputProps={{
                    "data-testid": "email",
                  }}
                />
                <TextField
                  type="number"
                  name="contactNumber"
                  label="Contact"
                  {...register("contactNumber", { required: true })}
                  error={!!errors.contactNumber}
                  helperText={errors.contactNumber?.message}
                  inputProps={{
                    "data-testid": "contactNumber",
                  }}
                />

                <TextField
                  name="address"
                  label="Address"
                  {...register("address", { required: true })}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  inputProps={{
                    "data-testid": "address",
                  }}
                />

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label-type">
                      Occupation
                    </InputLabel>
                    <Select
                      {...register("occupation")}
                      labelId="demo-simple-select-label-type"
                      label="Occupation"
                      error={Boolean(errors.type)}
                      value={occupation}
                      inputProps={{
                        "data-testid": "occupation",
                      }}
                    >
                      <MenuItem value="student">Student</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="businessman">Businessman</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label-type">
                      Education
                    </InputLabel>
                    <Select
                      {...register("education")}
                      labelId="demo-simple-select-label-type"
                      label="Education"
                      error={Boolean(errors.type)}
                      value={education}
                      inputProps={{
                        "data-testid": "education",
                      }}
                    >
                      <MenuItem value="primary_school">Primary School</MenuItem>
                      <MenuItem value="high_school">High School</MenuItem>
                      <MenuItem value="diploma">Diploma</MenuItem>
                      <MenuItem value="bachelor">Bachelor</MenuItem>
                      <MenuItem value="master">Masters</MenuItem>
                      <MenuItem value="doctorate">Doctorate</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <TextField
                  name="designation"
                  label="Designation"
                  {...register("designation", { required: true })}
                  error={!!errors.work}
                  helperText={errors.work?.message}
                  inputProps={{
                    "data-testid": "designation",
                  }}
                />

                <TextField
                  type="number"
                  name="annualIncome"
                  label="Annual Income"
                  {...register("annualIncome", { required: true })}
                  error={!!errors.annualIncome}
                  helperText={errors.annualIncome?.message}
                  inputProps={{
                    "data-testid": "annualIncome",
                  }}
                />

                <InputLabel htmlFor="logo">Disability Certificate</InputLabel>

                <input
                  onChange={handleFileChange}
                  ref={fileRef}
                  type="file"
                  // accept=".xlsx, .xls, .csv"
                  hidden
                  data-testid="file-input"
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px dashed #D9D9D9",
                    borderRadius: "10px",
                    padding: "20px",
                    justifyContent: "center",
                    flexDirection: "column",
                    cursor: "pointer",
                    height: 200,
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <UploadFile
                    sx={{
                      fontSize: "50px",
                      color: "#D9D9D9",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: 0.5,
                      textAlign: "center",
                      fontStyle: "italic",
                    }}
                  >
                    {selectedFile ? selectedFile.name : "(No file selected)"}
                  </Typography>
                  <Button
                    sx={{
                      marginTop: 3,
                      boxShadow: "none",
                    }}
                    variant="outlined"
                    onClick={() => fileRef.current.click()}
                  >
                    Browse
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: 0.5,
                      textAlign: "center",
                    }}
                  >
                    Or drag here{" "}
                    <Typography
                      component={"span"}
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                      }}
                    >
                      (.pdf,.jpeg,.png)
                    </Typography>
                  </Typography>
                </Box>
                {fileError && (
                  <FormHelperText error={Boolean(fileError)}>
                    {fileError}
                  </FormHelperText>
                )}

                <TextField
                  type="number"
                  name="disabilityPercentage"
                  label="Percentage of Disability"
                  {...register("disabilityPercentage", { required: true })}
                  error={!!errors.disabilityPercentage}
                  helperText={errors.disabilityPercentage?.message}
                  inputProps={{
                    "data-testid": "disabilityPercentage",
                  }}
                />

                <TextField
                  name="otherDisabilities"
                  label="Other Disabilities"
                  {...register("otherDisabilities", { required: true })}
                  error={!!errors.otherDisabilities}
                  helperText={errors.otherDisabilities?.message}
                  inputProps={{
                    "data-testid": "otherDisabilities",
                  }}
                />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  loading={isUpdateLoading}
                >
                  Submit
                </LoadingButton>
              </Stack>
            </form>
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}
